import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

interface FieldProps {
  label: ReactNode;
  htmlFor: string;
  error?: string | undefined;
  errorId?: string;
  hint?: ReactNode;
  children: ReactNode;
}

/** `.field` = label + control + error + hint. */
export function Field({ label, htmlFor, error, errorId, hint, children }: FieldProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      <p className="field__error" id={errorId} role="alert" hidden={!error}>
        {error}
      </p>
      {hint !== undefined && <p className="field__hint">{hint}</p>}
    </div>
  );
}

interface SearchInputProps {
  id?: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

/** `label.input-wrap--icon.search`. */
export function SearchInput({ id, label, placeholder, value, onChange }: SearchInputProps) {
  return (
    <label className="input-wrap input-wrap--icon search">
      <span className="visually-hidden">{label}</span>
      <Icon name="search" size="sm" />
      <input
        className="input"
        id={id}
        type="search"
        placeholder={placeholder}
        autoComplete="off"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </label>
  );
}

/** `p.alert.alert--{tone}` with an icon. */
export function Alert({
  tone = 'info',
  icon = 'alert',
  children,
}: {
  tone?: 'error' | 'info' | 'success';
  icon?: IconName;
  children: ReactNode;
}) {
  return (
    <p className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <Icon name={icon} size="sm" />
      <span>{children}</span>
    </p>
  );
}
